// Structured view for the LLM-generated playtest match recap.
//
// The summary comes back as markdown with exactly four `## ` headings
// (How the match was won / Strengths & weaknesses / Mana curve
// performance / AI artifacts). We split on those headings, render each
// section as a styled Panel with an icon, and feed bodies to the shared
// Markdown component so card names (wrapped in {curly braces} on V3+)
// get hover popovers.
//
// If the model skips a section or emits extra sections, we render what
// we got — no schema enforcement, no hidden content. The LLM is
// sometimes going to surprise us and the user should see it.

import type { ReactNode } from "react";
import {
  Mountain,
  AlertOctagon,
  ShieldCheck,
  Trophy,
  Layers,
} from "lucide-react";
import Markdown from "./Markdown";
import { cn } from "../lib/cn";

interface Props {
  /** Raw markdown text from the /summary endpoint. */
  summary: string;
}

interface ParsedSection {
  heading: string;
  body: string;
}

// Match the four expected headings case-insensitively so minor
// capitalization drift doesn't break the styled view. Order matters:
// a longer heading must match before a shorter one that's a prefix.
const KNOWN_HEADINGS: Array<{
  re: RegExp;
  icon: ReactNode;
  accent: "accent" | "warn" | "muted";
  title: string;
}> = [
  {
    re: /^how the match was won$/i,
    icon: <Trophy size={14} strokeWidth={1.75} />,
    accent: "accent",
    title: "How the match was won",
  },
  {
    re: /^strengths\s*&?\s*weaknesses$/i,
    icon: <ShieldCheck size={14} strokeWidth={1.75} />,
    accent: "accent",
    title: "Strengths & weaknesses",
  },
  {
    re: /^mana curve performance$/i,
    icon: <Mountain size={14} strokeWidth={1.75} />,
    accent: "muted",
    title: "Mana curve performance",
  },
  {
    re: /^ai artifacts$/i,
    icon: <AlertOctagon size={14} strokeWidth={1.75} />,
    accent: "warn",
    title: "AI artifacts",
  },
];

/**
 * Split raw markdown on `## ` headings. Content above the first heading
 * (if any) becomes a preamble panel; trailing content after the last
 * heading belongs to that heading's body.
 */
function parseSections(text: string): {
  preamble: string;
  sections: ParsedSection[];
} {
  const lines = text.split("\n");
  const sections: ParsedSection[] = [];
  let preambleLines: string[] = [];
  let current: ParsedSection | null = null;
  for (const line of lines) {
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      if (current) sections.push(current);
      else preambleLines = preambleLines.filter((l) => l.trim());
      current = { heading: m[1]!, body: "" };
      continue;
    }
    if (current) {
      current.body += (current.body ? "\n" : "") + line;
    } else {
      preambleLines.push(line);
    }
  }
  if (current) sections.push(current);
  // Trim leading/trailing whitespace on each body so Markdown doesn't
  // emit empty paragraphs.
  for (const s of sections) s.body = s.body.trim();
  return { preamble: preambleLines.join("\n").trim(), sections };
}

export default function SummaryView({ summary }: Props) {
  const { preamble, sections } = parseSections(summary);

  // Fallback: no headings detected → render the whole thing in a single
  // panel. Keeps the page readable if the model misbehaves.
  if (sections.length === 0) {
    return (
      <div className="px-5 py-4">
        <div className="text-sm leading-relaxed text-fg space-y-2">
          <Markdown text={summary} />
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 py-4 space-y-4">
      {preamble && (
        <div className="text-sm leading-relaxed text-fg-muted space-y-2">
          <Markdown text={preamble} />
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {sections.map((s, i) => {
          const known = KNOWN_HEADINGS.find((h) => h.re.test(s.heading.trim()));
          return (
            <Panel
              key={i}
              title={known?.title ?? s.heading}
              icon={known?.icon ?? <Layers size={14} strokeWidth={1.75} />}
              accent={known?.accent ?? "muted"}
            >
              <Markdown text={s.body} />
            </Panel>
          );
        })}
      </div>
    </div>
  );
}

function Panel({
  title,
  icon,
  children,
  accent,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  accent: "accent" | "warn" | "muted";
}) {
  return (
    <section className="rounded-md border border-border bg-bg-surface/40 p-4">
      <header
        className={cn(
          "flex items-center gap-2 text-[11px] uppercase tracking-wider font-medium mb-2",
          accent === "accent" && "text-accent",
          accent === "warn" && "text-warn",
          accent === "muted" && "text-fg-subtle",
        )}
      >
        {icon}
        <span>{title}</span>
      </header>
      <div className="text-sm leading-relaxed text-fg space-y-2">
        {children}
      </div>
    </section>
  );
}
